import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PaymentList.css";

const API_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:5000";

const MONTHS_AL = [
  "Janar",
  "Shkurt",
  "Mars",
  "Prill",
  "Maj",
  "Qershor",
  "Korrik",
  "Gusht",
  "Shtator",
  "Tetor",
  "Nëntor",
  "Dhjetor",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2024 + 2 }, (_, i) => 2024 + i);

export default function PaymentList() {
  const navigate = useNavigate();

  const isParkingOnly = (payment) =>
    !payment?.apartment_name &&
    (
      (Array.isArray(payment?.tenant_parkings) && payment.tenant_parkings.length > 0) ||
      Boolean(payment?.tenant_has_parking)
    );

  const parkingNumbers = (payment) => {
    const nums = Array.isArray(payment?.tenant_parkings)
      ? payment.tenant_parkings
          .map((p) => p?.numero_parcheggio)
          .filter(Boolean)
      : [];

    if (nums.length > 0) return nums.join(", ");
    return payment?.tenant_parking_number || "-";
  };

  const [payments, setPayments] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState(CURRENT_YEAR);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTenant, setSearchTenant] = useState("");
  const [searchApartment, setSearchApartment] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    luce: "",
    acqua: "",
    month: "",
    year: CURRENT_YEAR,
  });

  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesPayment, setNotesPayment] = useState(null);
  const notesTextareaRef = useRef(null);

  const [dedModalOpen, setDedModalOpen] = useState(false);
  const [dedPayment, setDedPayment] = useState(null);
  const [dedDesc, setDedDesc] = useState("");
  const [dedAmount, setDedAmount] = useState("");

  const [extraModalOpen, setExtraModalOpen] = useState(false);
  const [extraPayment, setExtraPayment] = useState(null);
  const [extraDesc, setExtraDesc] = useState("");
  const [extraAmount, setExtraAmount] = useState("");

  const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem("access_token");
    return isJson
      ? {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      : {
          Authorization: `Bearer ${token}`,
        };
  };

  const mobileEditInputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #cdd3e1",
    background: "white",
    fontSize: "14px",
    color: "#1d2a5b",
    boxSizing: "border-box",
  };

  const mobileEditLabelStyle = {
    fontSize: "12px",
    fontWeight: 700,
    color: "#64748b",
    marginBottom: "6px",
    display: "block",
  };

  const mobileEditBoxStyle = {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "10px 12px",
  };

  const handleUnauthorized = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    navigate("/login", { replace: true });
  };

  const handleApiError = async (res, fallbackMessage) => {
    if (res.status === 401 || res.status === 422) {
      handleUnauthorized();
      return { unauthorized: true, data: null };
    }

    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    if (!res.ok) {
      setError(data?.error || fallbackMessage);
      return { unauthorized: false, data };
    }

    return { unauthorized: false, data };
  };

  const loadPayments = async () => {
    setError("");

    try {
      const res = await fetch(`${API_URL}/payments/`, {
        headers: getAuthHeaders(false),
        cache: "no-store",
      });

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      const data = await res.json();

      if (!res.ok) {
        setError(data?.error || "Gabim gjatë ngarkimit të pagesave.");
        return;
      }

      const normalized = Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            pagato:
              item.pagato ??
              item.paid ??
              item.is_paid ??
              item.status === "paid" ??
              false,
          }))
        : [];

      const sorted = [...normalized].sort((a, b) => {
        const an = Number(a.apartment_name ?? 0);
        const bn = Number(b.apartment_name ?? 0);
        return an - bn;
      });

      setPayments(sorted);
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  useEffect(() => {
    if (!filterMonth) {
      setPayments([]);
      return;
    }
    loadPayments();
  }, [filterMonth, filterYear]);

  const filteredPayments = filterMonth
    ? payments.filter((p) => {
        const sameMonth =
          p.month === filterMonth && parseInt(p.year) === parseInt(filterYear);

        if (!sameMonth) return false;

        if (filterStatus === "paid" && Boolean(p.pagato) !== true) return false;
        if (filterStatus === "unpaid" && Boolean(p.pagato) !== false) return false;

        const tenantName = (p.tenant_name || "").toLowerCase();
        const apartmentName = String(p.apartment_name || "").toLowerCase();

        const tenantSearchValue = searchTenant.trim().toLowerCase();
        const apartmentSearchValue = searchApartment.trim().toLowerCase();

        if (tenantSearchValue && !tenantName.includes(tenantSearchValue)) return false;
        if (apartmentSearchValue && !apartmentName.includes(apartmentSearchValue)) return false;

        return true;
      })
    : [];

  const startEdit = (payment) => {
    setEditingId(payment.id);
    setEditForm({
      luce: payment.luce ?? "",
      acqua: payment.acqua ?? "",
      month: payment.month ?? "",
      year: payment.year ?? CURRENT_YEAR,
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (payment) => {
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${payment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: parseFloat(editForm.luce) || 0,
          acqua: parseFloat(editForm.acqua) || 0,
          month: editForm.month,
          year: parseInt(editForm.year),
          pagato: payment.pagato,
          notes: payment.notes || "",
          deduction_description: payment.deduction_description || "",
          deduction_amount: payment.deduction_amount || 0,
          extra_description: payment.extra_description || "",
          extra_amount: payment.extra_amount || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë përditësimit të pagesës.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Pagesa u përditësua me sukses.");
      await loadPayments();
      setEditingId(null);
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const deletePayment = async (id) => {
    setError("");
    setSuccess("");

    if (!window.confirm("Je i sigurt që dëshiron të fshish këtë pagesë?")) return;
    if (!window.confirm("Konfirmo përfundimisht fshirjen")) return;

    try {
      const res = await fetch(`${API_URL}/payments/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(false),
      });

      const result = await handleApiError(res, "Gabim gjatë fshirjes së pagesës.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Pagesa u fshi me sukses.");
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const togglePagato = async (payment) => {
    setError("");
    setSuccess("");

    const newPagato = !Boolean(payment.pagato);

    try {
      const res = await fetch(`${API_URL}/payments/${payment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: payment.luce,
          acqua: payment.acqua,
          month: payment.month,
          year: payment.year,
          pagato: newPagato,
          notes: payment.notes || "",
          deduction_description: payment.deduction_description || "",
          deduction_amount: payment.deduction_amount || 0,
          extra_description: payment.extra_description || "",
          extra_amount: payment.extra_amount || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë përditësimit të statusit.");
      if (result.unauthorized || !res.ok) return;

      setPayments((prev) =>
        prev.map((item) =>
          item.id === payment.id ? { ...item, pagato: newPagato } : item
        )
      );

      setSuccess("Statusi i pagesës u përditësua.");
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const openPdfWithAuth = async (url, filename = "document.pdf") => {
    setError("");

    try {
      const res = await fetch(url, {
        headers: getAuthHeaders(false),
      });

      if (res.status === 401 || res.status === 422) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        let message = "Gabim gjatë gjenerimit të dokumentit.";
        try {
          const data = await res.json();
          message = data?.error || message;
        } catch {
          // ignore
        }
        setError(message);
        return;
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, "_blank");

      if (!newWindow) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 10000);
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const generateSinglePDF = async (paymentId) => {
    await openPdfWithAuth(`${API_URL}/export/pdf/${paymentId}`, `pagesa-${paymentId}.pdf`);
  };

  const generateMonthlyPDF = async () => {
    if (!filterMonth) {
      setError("Zgjidh një muaj.");
      return;
    }

    await openPdfWithAuth(
      `${API_URL}/export/pdf-month/${filterYear}/${filterMonth}`,
      `raport-${filterMonth}-${filterYear}.pdf`
    );
  };

  const generateMonthlyZipPDFs = async () => {
    if (!filterMonth) {
      setError("Zgjidh një muaj.");
      return;
    }

    await openPdfWithAuth(
      `${API_URL}/export/pdf-month-all/${filterYear}/${filterMonth}`,
      `Faturat_${filterMonth}_${filterYear}.zip`
    );
  };

  const sendWhatsApp = (payment) => {
    const phone = payment.tenant_phone?.replace(/\s+/g, "").replace("+", "");
    if (!phone) {
      setError("Ky qiramarrës nuk ka numër telefoni.");
      return;
    }

    const dedLine =
      payment.deduction_amount > 0
        ? `\n- Zbritje (${payment.deduction_description || "Punë"}): -${payment.deduction_amount} Lek`
        : "";

    const extraLine =
      payment.extra_amount > 0
        ? `\n- Plus pagesë (${payment.extra_description || "Shtesë"}): +${payment.extra_amount} Lek`
        : "";

    const message = isParkingOnly(payment)
      ? `
Pershendetje ${payment.tenant_name},
Fature mujore parking (${payment.month} ${payment.year})
- Parking: ${parkingNumbers(payment)}
Totali: ${payment.total} Lek
Ju faleminderit!
      `.trim()
      : `
Pershendetje ${payment.tenant_name},
Fature mujore (${payment.month} ${payment.year})
- Drita: ${payment.luce} Lek
- Uji: ${payment.acqua} Lek${dedLine}${extraLine}
Totali: ${payment.total} Lek
Ju faleminderit!
      `.trim();

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`);
  };

  const openNotesModal = (payment) => {
    setNotesPayment(payment);
    setNotesDraft(payment.notes ?? "");
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNotesPayment(null);
    setNotesDraft("");
  };

  const saveNotesModal = async () => {
    if (!notesPayment) return;

    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${notesPayment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: notesPayment.luce,
          acqua: notesPayment.acqua,
          month: notesPayment.month,
          year: notesPayment.year,
          pagato: notesPayment.pagato,
          notes: notesDraft,
          deduction_description: notesPayment.deduction_description || "",
          deduction_amount: notesPayment.deduction_amount || 0,
          extra_description: notesPayment.extra_description || "",
          extra_amount: notesPayment.extra_amount || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë ruajtjes së shënimeve.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Shënimet u ruajtën me sukses.");
      closeNotesModal();
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const openDedModal = (payment) => {
    setDedPayment(payment);
    setDedDesc(payment.deduction_description || "");
    setDedAmount(payment.deduction_amount > 0 ? String(payment.deduction_amount) : "");
    setDedModalOpen(true);
  };

  const closeDedModal = () => {
    setDedModalOpen(false);
    setDedPayment(null);
    setDedDesc("");
    setDedAmount("");
  };

  const saveDedModal = async () => {
    if (!dedPayment) return;

    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${dedPayment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: dedPayment.luce,
          acqua: dedPayment.acqua,
          month: dedPayment.month,
          year: dedPayment.year,
          pagato: dedPayment.pagato,
          notes: dedPayment.notes || "",
          deduction_description: dedDesc.trim(),
          deduction_amount: parseFloat(dedAmount) || 0,
          extra_description: dedPayment.extra_description || "",
          extra_amount: dedPayment.extra_amount || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë ruajtjes së zbritjes.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Zbritja u ruajt me sukses.");
      closeDedModal();
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const removeDed = async () => {
    if (!dedPayment) return;
    if (!window.confirm("Konfirmo heqjen e zbritjes?")) return;

    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${dedPayment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: dedPayment.luce,
          acqua: dedPayment.acqua,
          month: dedPayment.month,
          year: dedPayment.year,
          pagato: dedPayment.pagato,
          notes: dedPayment.notes || "",
          deduction_description: "",
          deduction_amount: 0,
          extra_description: dedPayment.extra_description || "",
          extra_amount: dedPayment.extra_amount || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë heqjes së zbritjes.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Zbritja u hoq me sukses.");
      closeDedModal();
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const openExtraModal = (payment) => {
    setExtraPayment(payment);
    setExtraDesc(payment.extra_description || "");
    setExtraAmount(payment.extra_amount > 0 ? String(payment.extra_amount) : "");
    setExtraModalOpen(true);
  };

  const closeExtraModal = () => {
    setExtraModalOpen(false);
    setExtraPayment(null);
    setExtraDesc("");
    setExtraAmount("");
  };

  const saveExtraModal = async () => {
    if (!extraPayment) return;

    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${extraPayment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: extraPayment.luce,
          acqua: extraPayment.acqua,
          month: extraPayment.month,
          year: extraPayment.year,
          pagato: extraPayment.pagato,
          notes: extraPayment.notes || "",
          deduction_description: extraPayment.deduction_description || "",
          deduction_amount: extraPayment.deduction_amount || 0,
          extra_description: extraDesc.trim(),
          extra_amount: parseFloat(extraAmount) || 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë ruajtjes së plus pagesës.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Plus pagesa u ruajt me sukses.");
      closeExtraModal();
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  const removeExtra = async () => {
    if (!extraPayment) return;
    if (!window.confirm("Konfirmo heqjen e plus pagesës?")) return;

    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_URL}/payments/${extraPayment.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          luce: extraPayment.luce,
          acqua: extraPayment.acqua,
          month: extraPayment.month,
          year: extraPayment.year,
          pagato: extraPayment.pagato,
          notes: extraPayment.notes || "",
          deduction_description: extraPayment.deduction_description || "",
          deduction_amount: extraPayment.deduction_amount || 0,
          extra_description: "",
          extra_amount: 0,
        }),
      });

      const result = await handleApiError(res, "Gabim gjatë heqjes së plus pagesës.");
      if (result.unauthorized || !res.ok) return;

      setSuccess("Plus pagesa u hoq me sukses.");
      closeExtraModal();
      await loadPayments();
    } catch {
      setError("Nuk u arrit lidhja me serverin.");
    }
  };

  useEffect(() => {
    if (!notesModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeNotesModal();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") saveNotesModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notesModalOpen, notesDraft, notesPayment]);

  useEffect(() => {
    if (!dedModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDedModal();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") saveDedModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dedModalOpen, dedDesc, dedAmount, dedPayment]);

  useEffect(() => {
    if (!extraModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeExtraModal();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") saveExtraModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [extraModalOpen, extraDesc, extraAmount, extraPayment]);

  useEffect(() => {
    if (!notesModalOpen && !dedModalOpen && !extraModalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [notesModalOpen, dedModalOpen, extraModalOpen]);

  useEffect(() => {
    if (!notesModalOpen) return;
    requestAnimationFrame(() => {
      notesTextareaRef.current?.focus();
    });
  }, [notesModalOpen]);

  return (
    <div className="payment-list-container">
      <h2>Lista e Pagesave</h2>

      {error && (
        <div style={{ marginBottom: "15px", color: "#b00020", fontWeight: "600" }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ marginBottom: "15px", color: "green", fontWeight: "600" }}>
          {success}
        </div>
      )}

      <div className="filter-bar">
        <select
          className="month-select"
          value={filterYear}
          onChange={(e) => setFilterYear(parseInt(e.target.value))}
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <select
          className="month-select"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
        >
          <option value="">-- Zgjidh muajin --</option>
          {MONTHS_AL.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          className="month-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Të gjitha pagesat</option>
          <option value="paid">Vetëm të paguarat</option>
          <option value="unpaid">Vetëm të papaguarat</option>
        </select>

        <input
          type="text"
          className="month-select filter-search"
          placeholder="🔎 Kërko sipas emrit të qiramarrësit..."
          value={searchTenant}
          onChange={(e) => setSearchTenant(e.target.value)}
        />

        <input
          type="text"
          className="month-select filter-search"
          placeholder="🏢 Kërko sipas apartamentit..."
          value={searchApartment}
          onChange={(e) => setSearchApartment(e.target.value)}
        />

        {filterMonth && (
          <>
            <button className="monthly-btn" onClick={generateMonthlyPDF}>
              📄 PDF {filterMonth} {filterYear}
            </button>

            <button className="monthly-btn" onClick={generateMonthlyZipPDFs}>
              📦 ZIP Fatura {filterMonth} {filterYear}
            </button>
          </>
        )}
      </div>

      <table className="payment-table">
        <thead>
          <tr>
            <th>Qiramarrësi</th>
            <th>Apartamenti</th>
            <th>Muaji</th>
            <th>Drita</th>
            <th>Uji</th>
            <th>Totali</th>
            <th>−</th>
            <th>+</th>
            <th>Paguar</th>
            <th>Note</th>
            <th>Veprime</th>
          </tr>
        </thead>

        <tbody>
          {filteredPayments.length === 0 ? (
            <tr>
              <td colSpan={11} style={{ textAlign: "center", padding: 20 }}>
                {filterMonth
                  ? "Nuk ka pagesa që përputhen me filtrat e zgjedhur."
                  : "Zgjidh një muaj dhe vit për të parë listën e pagesave."}
              </td>
            </tr>
          ) : (
            filteredPayments.map((p) => {
              const isEditing = editingId === p.id;
              const hasDed = Number(p.deduction_amount || 0) > 0;
              const hasExtra = Number(p.extra_amount || 0) > 0;
              const isPaid = Boolean(p.pagato);

              return (
                <tr key={p.id}>
                  <td>{p.tenant_name}</td>
                  <td>
                    {isParkingOnly(p) ? `Parking ${parkingNumbers(p)}` : p.apartment_name}
                  </td>

                  <td>
                    {isEditing ? (
                      <select
                        className="edit-month-select"
                        value={editForm.month}
                        onChange={(e) =>
                          setEditForm({ ...editForm, month: e.target.value })
                        }
                      >
                        {MONTHS_AL.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    ) : (
                      `${p.month} ${p.year}`
                    )}
                  </td>

                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.luce}
                        onChange={(e) =>
                          setEditForm({ ...editForm, luce: e.target.value })
                        }
                      />
                    ) : (
                      isParkingOnly(p) ? "—" : p.luce
                    )}
                  </td>

                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.acqua}
                        onChange={(e) =>
                          setEditForm({ ...editForm, acqua: e.target.value })
                        }
                      />
                    ) : (
                      isParkingOnly(p) ? "—" : p.acqua
                    )}
                  </td>

                  <td>{p.total}</td>

                  <td>
                    <div className="note-btn-wrapper">
                      <button
                        onClick={() => openDedModal(p)}
                        className={`payment-mini-minus-btn ${hasDed ? "has-value" : ""}`}
                        title="Zbritje"
                      >
                        -
                      </button>

                      {hasDed && (
                        <div className="note-tooltip">
                          {p.deduction_description || "Zbritje"}
                          {"\n"}- {p.deduction_amount} Lek
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className="note-btn-wrapper">
                      <button
                        onClick={() => openExtraModal(p)}
                        className={`payment-mini-plus-btn ${hasExtra ? "has-value" : ""}`}
                        title="Plus pagesë"
                      >
                        +
                      </button>

                      {hasExtra && (
                        <div className="note-tooltip">
                          {p.extra_description || "Shtesë"}
                          {"\n"}+ {p.extra_amount} Lek
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <button
                      className={`pay-toggle-btn ${isPaid ? "paid" : "unpaid"}`}
                      onClick={() => togglePagato(p)}
                    >
                      {isPaid ? "✅ Paguar" : "❌ Pa paguar"}
                    </button>
                  </td>

                  <td>
                    <div className="note-btn-wrapper">
                      <button className="pdf-btn" onClick={() => openNotesModal(p)}>
                        🗒️ {p.notes && p.notes.trim() !== "" ? "📝" : "➕"}
                      </button>
                      <div className="note-tooltip">
                        {p.notes && p.notes.trim() !== "" ? p.notes : "Nuk ka shënime"}
                      </div>
                    </div>
                  </td>

                  <td>
                    {isEditing ? (
                      <>
                        <button className="save-btn" onClick={() => saveEdit(p)}>
                          💾 Ruaj
                        </button>
                        <button className="delete-btn" onClick={cancelEdit}>
                          ✖ Anulo
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="edit-btn" onClick={() => startEdit(p)}>
                          🖊️ Ndrysho
                        </button>
                        <button className="pdf-btn" onClick={() => generateSinglePDF(p.id)}>
                          📄 PDF
                        </button>
                        <button
                          className="whatsapp-round-btn"
                          onClick={() => sendWhatsApp(p)}
                          title="WhatsApp"
                        >
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                            alt="WhatsApp"
                            className="whatsapp-round-icon"
                          />
                        </button>
                        <button className="delete-btn" onClick={() => deletePayment(p.id)}>
                          🗑️ Fshi
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <div className="payment-cards">
        {filteredPayments.length === 0 ? (
          <div className="payment-empty-mobile">
            {filterMonth
              ? "Nuk ka pagesa që përputhen me filtrat e zgjedhur."
              : "Zgjidh një muaj dhe vit për të parë listën e pagesave."}
          </div>
        ) : (
          filteredPayments.map((p) => {
            const hasDed = Number(p.deduction_amount || 0) > 0;
            const hasExtra = Number(p.extra_amount || 0) > 0;
            const isPaid = Boolean(p.pagato);
            const isEditing = editingId === p.id;

            return (
              <div key={p.id} className={`payment-card ${isPaid ? "paid-card" : "unpaid-card"}`}>
                <div className="payment-card-top">
                  <div>
                    <div className="payment-card-tenant">👤 {p.tenant_name}</div>
                    <div className="payment-card-apartment">
                      {isParkingOnly(p)
                        ? `🅿️ Parking ${parkingNumbers(p)}`
                        : `🏢 Apt ${p.apartment_name}`}
                    </div>
                  </div>

                  {!isEditing && (
                    <button
                      className={`pay-toggle-btn mobile-status-btn ${isPaid ? "paid" : "unpaid"}`}
                      onClick={() => togglePagato(p)}
                    >
                      {isPaid ? "✅ Paguar" : "❌ Pa paguar"}
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "10px",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={mobileEditBoxStyle}>
                        <label style={mobileEditLabelStyle}>📅 Muaji</label>
                        <select
                          value={editForm.month}
                          onChange={(e) =>
                            setEditForm({ ...editForm, month: e.target.value })
                          }
                          style={mobileEditInputStyle}
                        >
                          {MONTHS_AL.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={mobileEditBoxStyle}>
                        <label style={mobileEditLabelStyle}>💡 Drita</label>
                        <input
                          type="number"
                          value={editForm.luce}
                          onChange={(e) =>
                            setEditForm({ ...editForm, luce: e.target.value })
                          }
                          style={mobileEditInputStyle}
                        />
                      </div>

                      <div style={mobileEditBoxStyle}>
                        <label style={mobileEditLabelStyle}>💧 Uji</label>
                        <input
                          type="number"
                          value={editForm.acqua}
                          onChange={(e) =>
                            setEditForm({ ...editForm, acqua: e.target.value })
                          }
                          style={mobileEditInputStyle}
                        />
                      </div>

                      <div
                        style={{
                          ...mobileEditBoxStyle,
                          background: "#eff6ff",
                          borderColor: "#bfdbfe",
                        }}
                      >
                        <label style={mobileEditLabelStyle}>💰 Totali aktual</label>
                        <div style={{ fontSize: "18px", fontWeight: 800, color: "#1d4ed8" }}>
                          {p.total} Lek
                        </div>
                      </div>
                    </div>

                    <div className="payment-card-action-row payment-card-action-row-bottom">
                      <button className="save-btn mobile-action-btn" onClick={() => saveEdit(p)}>
                        💾 Ruaj
                      </button>
                      <button className="delete-btn mobile-action-btn" onClick={cancelEdit}>
                        ✖ Anulo
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="payment-card-grid">
                      <div className="payment-card-item">
                        <span className="payment-card-label">📅 Muaji</span>
                        <span className="payment-card-value">
                          {p.month} {p.year}
                        </span>
                      </div>

                      <div className="payment-card-item">
                        <span className="payment-card-label">💡 Drita</span>
                        <span className="payment-card-value">{p.luce} Lek</span>
                      </div>

                      <div className="payment-card-item">
                        <span className="payment-card-label">💧 Uji</span>
                        <span className="payment-card-value">{p.acqua} Lek</span>
                      </div>

                      <div className="payment-card-item payment-card-total-box">
                        <span className="payment-card-label">💰 Totali</span>
                        <span className="payment-card-total">{p.total} Lek</span>
                      </div>
                    </div>

                    {(hasDed || hasExtra) && (
                      <div className="payment-card-adjustments">
                        {hasDed && (
                          <div className="adjustment-row minus-row">
                            <span>➖ {p.deduction_description || "Zbritje"}</span>
                            <strong>- {p.deduction_amount} Lek</strong>
                          </div>
                        )}

                        {hasExtra && (
                          <div className="adjustment-row plus-row">
                            <span>➕ {p.extra_description || "Shtesë"}</span>
                            <strong>+ {p.extra_amount} Lek</strong>
                          </div>
                        )}
                      </div>
                    )}

                    {p.notes && p.notes.trim() !== "" && (
                      <div className="payment-card-notes">
                        <div className="payment-card-notes-title">🗒️ Shënime</div>
                        <div className="payment-card-notes-text">{p.notes}</div>
                      </div>
                    )}

                    <div className="payment-card-action-row">
                      <button
                        className="payment-mini-minus-btn"
                        onClick={() => openDedModal(p)}
                        title="Zbritje"
                      >
                        -
                      </button>

                      <button
                        className="payment-mini-plus-btn"
                        onClick={() => openExtraModal(p)}
                        title="Plus pagesë"
                      >
                        +
                      </button>

                      <button className="pdf-btn mobile-action-btn" onClick={() => openNotesModal(p)}>
                        🗒️ Note
                      </button>

                      <button className="pdf-btn mobile-action-btn" onClick={() => generateSinglePDF(p.id)}>
                        📄 PDF
                      </button>
                    </div>

                    <div className="payment-card-action-row payment-card-action-row-bottom">
                      <button className="edit-btn mobile-action-btn" onClick={() => startEdit(p)}>
                        🖊️ Ndrysho
                      </button>

                      <button
                        className="whatsapp-mobile-btn"
                        onClick={() => sendWhatsApp(p)}
                        title="WhatsApp"
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                          alt="WhatsApp"
                          className="whatsapp-round-icon"
                        />
                        WhatsApp
                      </button>

                      <button className="delete-btn mobile-action-btn" onClick={() => deletePayment(p.id)}>
                        🗑️ Fshi
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {dedModalOpen && (
        <div className="notes-overlay" onClick={closeDedModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-header">
              <div className="notes-title">➖ Zbritje — {dedPayment?.tenant_name}</div>
              <div className="notes-subtitle">
                {dedPayment?.month} {dedPayment?.year} · Apt {dedPayment?.apartment_name}
              </div>
              <button className="delete-btn notes-close-btn" onClick={closeDedModal}>
                ✖
              </button>
            </div>

            <div className="notes-body">
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1d2a5b" }}>
                  Përshkrimi i punës
                </label>
                <input
                  type="text"
                  value={dedDesc}
                  onChange={(e) => setDedDesc(e.target.value)}
                  placeholder="Punë elektricisti, Hidraulik…"
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #cdd3e1",
                    fontSize: 14,
                    color: "#1d2a5b",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1d2a5b" }}>
                  Shuma e zbritjes (Lek)
                </label>
                <input
                  type="number"
                  value={dedAmount}
                  onChange={(e) => setDedAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #cdd3e1",
                    fontSize: 14,
                    color: "#1d2a5b",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {Number(dedPayment?.deduction_amount || 0) > 0 && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fca5a5",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#b91c1c",
                  }}
                >
                  Zbritja aktuale: <b>{dedPayment.deduction_description || "—"}</b> —{" "}
                  {dedPayment.deduction_amount} Lek
                </div>
              )}

              <div className="notes-hint">Ctrl+Enter për të ruajtur · Esc për të mbyllur</div>
            </div>

            <div className="notes-footer">
              <button className="save-btn" onClick={saveDedModal}>
                💾 Ruaj
              </button>
              {Number(dedPayment?.deduction_amount || 0) > 0 && (
                <button className="delete-btn" onClick={removeDed}>
                  🗑️ Hiq Zbritjen
                </button>
              )}
              <button className="pdf-btn" onClick={closeDedModal}>
                ✖ Mbyll
              </button>
            </div>
          </div>
        </div>
      )}

      {extraModalOpen && (
        <div className="notes-overlay" onClick={closeExtraModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-header">
              <div className="notes-title">➕ Plus pagesë — {extraPayment?.tenant_name}</div>
              <div className="notes-subtitle">
                {extraPayment?.month} {extraPayment?.year} · Apt {extraPayment?.apartment_name}
              </div>
              <button className="delete-btn notes-close-btn" onClick={closeExtraModal}>
                ✖
              </button>
            </div>

            <div className="notes-body">
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1d2a5b" }}>
                  Përshkrimi i shtesës
                </label>
                <input
                  type="text"
                  value={extraDesc}
                  onChange={(e) => setExtraDesc(e.target.value)}
                  placeholder="Kondicioner, Riparim, Detyrim i mbetur…"
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #cdd3e1",
                    fontSize: 14,
                    color: "#1d2a5b",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#1d2a5b" }}>
                  Shuma e plus pagesës (Lek)
                </label>
                <input
                  type="number"
                  value={extraAmount}
                  onChange={(e) => setExtraAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                  style={{
                    width: "100%",
                    marginTop: 6,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #cdd3e1",
                    fontSize: 14,
                    color: "#1d2a5b",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {Number(extraPayment?.extra_amount || 0) > 0 && (
                <div
                  style={{
                    background: "#eff6ff",
                    border: "1px solid #93c5fd",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#1d4ed8",
                  }}
                >
                  Plus pagesa aktuale: <b>{extraPayment.extra_description || "—"}</b> —{" "}
                  {extraPayment.extra_amount} Lek
                </div>
              )}

              <div className="notes-hint">Ctrl+Enter për të ruajtur · Esc për të mbyllur</div>
            </div>

            <div className="notes-footer">
              <button className="save-btn" onClick={saveExtraModal}>
                💾 Ruaj
              </button>
              {Number(extraPayment?.extra_amount || 0) > 0 && (
                <button className="delete-btn" onClick={removeExtra}>
                  🗑️ Hiq Plus Pagesën
                </button>
              )}
              <button className="pdf-btn" onClick={closeExtraModal}>
                ✖ Mbyll
              </button>
            </div>
          </div>
        </div>
      )}

      {notesModalOpen && (
        <div className="notes-overlay" onClick={closeNotesModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-header">
              <div className="notes-title">🗒️ Note — {notesPayment?.tenant_name}</div>
              <div className="notes-subtitle">
                {notesPayment?.month} {notesPayment?.year} · Apt {notesPayment?.apartment_name}
              </div>
              <button className="delete-btn notes-close-btn" onClick={closeNotesModal}>
                ✖
              </button>
            </div>

            <div className="notes-body">
              {notesPayment?.notes && <div className="notes-preview">{notesPayment.notes}</div>}

              <textarea
                ref={notesTextareaRef}
                className="notes-textarea"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Shkruaj shënimin këtu…"
                rows={6}
              />

              <div className="notes-hint">Ctrl+Enter për të ruajtur · Esc për të mbyllur</div>
            </div>

            <div className="notes-footer">
              <button className="save-btn" onClick={saveNotesModal}>
                💾 Ruaj
              </button>
              <button className="delete-btn" onClick={closeNotesModal}>
                ✖ Mbyll
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
